<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 550px)' : 560"
    persistent
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Deposit Invoice Settings</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          @click="showPreview = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="deposit-dialog-layout">
          <div class="deposit-form-pane">
            <v-form ref="depositForm" lazy-validation>
              <div class="pa-6">
                <v-select
                  v-model="localForm.type"
                  :items="typeItems"
                  label="Type *"
                  outlined
                  dense
                  :rules="typeRules"
                  :disabled="localForm.include_order_items || Boolean(selectedProforma)"
                />

                <v-switch
                  v-model="localForm.include_order_items"
                  label="Show all Sales Order items"
                  :disabled="advanceModeLocked"
                  :hint="selectedProforma
                    ? 'The item display mode is inherited from the linked proforma.'
                    : (hasMixedItemVat
                      ? 'Summary mode creates one Előleg/Adminisztrációs díj row per VAT rate. Itemized mode shows the Sales Order product names.'
                      : 'When enabled, item names and units follow the Sales Order language.')"
                  persistent-hint
                  inset
                  class="mt-0 mb-4"
                />

                <v-select
                  v-if="proformaOptions.length > 0"
                  v-model="localForm.proforma_id"
                  :items="proformaOptions"
                  item-text="text"
                  item-value="value"
                  label="Linked proforma (optional)"
                  outlined
                  dense
                  clearable
                  hint="Leave empty to create a standalone deposit invoice, or select a proforma to inherit its saved rows."
                  persistent-hint
                  class="mt-2"
                />

                <v-alert
                  v-else
                  dense
                  outlined
                  type="info"
                  class="mt-2 mb-0"
                >
                  No active proforma is available for this order. You can still create a deposit invoice without linking it.
                </v-alert>

                <v-alert
                  v-if="selectedProforma"
                  dense
                  outlined
                  type="success"
                  class="mt-2 mb-0"
                >
                  Selected proforma:
                  {{ selectedProforma.document_number || '-' }} ·
                  {{ formatDocumentAmount(selectedProforma.gross_amount) }} ·
                  {{ selectedProformaMode === 'itemized' ? 'itemized mode' : 'VAT-grouped summary mode' }}
                </v-alert>

                <v-alert
                  v-if="selectedProformaCompatibilityError"
                  dense
                  outlined
                  type="error"
                  class="mt-2 mb-0"
                >
                  {{ selectedProformaCompatibilityError }}
                </v-alert>

                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-radio-group
                      v-model="localForm.mode"
                      :disabled="Boolean(selectedProforma)"
                      label="Calc. mode *"
                      class="mt-0"
                      hide-details="auto"
                      row
                      :rules="modeRules"
                    >
                      <v-radio label="Percent" value="percent" />
                      <v-radio label="Amount" value="value" />
                    </v-radio-group>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-if="localForm.mode === 'percent'"
                      v-model.number="localForm.percent"
                      label="Percent *"
                      type="number"
                      suffix="%"
                      min="0"
                      max="100"
                      outlined
                      dense
                      :rules="percentRules"
                      :disabled="Boolean(selectedProforma)"
                    />

                    <v-text-field
                      v-else-if="localForm.mode === 'value'"
                      v-model.number="localForm.value"
                      label="Amount *"
                      type="number"
                      :suffix="computeSuffix"
                      min="0"
                      :max="invoiceDatas?.total_net ?? null"
                      outlined
                      dense
                      :hint="invoiceDatas ? 'Minimum: 0 - Maximum: ' + invoiceDatas.total_net : ''"
                      :rules="valueRules"
                      :disabled="Boolean(selectedProforma)"
                    />
                  </v-col>
                </v-row>

                <v-alert
                  v-if="hasMixedItemVat && !selectedProforma"
                  dense
                  outlined
                  type="info"
                  class="mb-4"
                >
                  This Sales Order contains multiple VAT rates ({{ itemVatSummary }}). In summary mode, one identically named {{ localForm.type || 'advance' }} row is created for each VAT rate. Enable itemized mode only when the product names should appear on the document.
                </v-alert>

                <v-alert
                  v-if="localForm.include_order_items"
                  dense
                  outlined
                  type="info"
                  class="mb-4"
                >
                  The selected amount will be distributed proportionally between all Sales Order items. Item names use the Sales Order language and each item keeps its own VAT rate<span v-if="itemVatSummary"> ({{ itemVatSummary }})</span>.
                </v-alert>

                <v-combobox
                  v-if="!localForm.include_order_items && !hasMixedItemVat"
                  v-model="localForm.vat"
                  :items="vatItems"
                  label="Tax rate *"
                  outlined
                  dense
                  clearable
                  :rules="vatRules"
                  :disabled="Boolean(selectedProforma)"
                  @change="normalizeVat"
                />

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.fulfillmentDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.fulfillmentDate"
                          label="Date of completion"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.fulfillmentDate = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.fulfillmentDate"
                        no-title
                        @input="dateMenus.fulfillmentDate = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2 mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('fulfillmentDate', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.paymentDeadline"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.paymentDeadline"
                          label="Payment due date"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.paymentDeadline = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.paymentDeadline"
                        no-title
                        @input="dateMenus.paymentDeadline = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2 mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('paymentDeadline', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="localForm.note"
                  label="Comment"
                  outlined
                  dense
                  auto-grow
                  rows="3"
                  hint="Optional"
                  persistent-hint
                />
              </div>
            </v-form>
          </div>

          <v-expand-x-transition>
            <div v-if="showPreview" class="deposit-preview-pane">
              <v-divider vertical />

              <div class="pa-6 preview-pane-inner">
                <InvoicePreviewPanel
                  document-type="deposit_invoice"
                  :order="invoiceDatas"
                  :settings="previewSettings"
                  :seller="seller"
                  :currency="computeSuffix"
                />
              </div>
            </div>
          </v-expand-x-transition>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          v-if="invoiceDatas"
          color="primary"
          class="mr-2"
          @click="openPreview"
          :disabled="loading"
        >
          {{ !showPreview ? 'Preview deposit invoice' : 'Close preview' }}
        </v-btn>

        <v-btn text 
          @click="closeDialog"
          :disabled="loading"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InvoicePreviewPanel from './InvoicePreviewPanel.vue'
import Swal from 'sweetalert2'

export default {
  name: 'DepositDialog',

  components: {
    InvoicePreviewPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({
        id: null,
        proforma_id: null,
        type: null,
        mode: null,
        percent: null,
        value: null,
        vat: null,
        include_order_items: false,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
      }),
    },
    invoiceDatas: {
      type: Object,
      default: () => null,
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
  },

  data() {
    return {
      showPreview: false,
      vatDefaultApplied: false,
      dateMenus: {
        fulfillmentDate: false,
        paymentDeadline: false,
        dueDate: false,
      },
      localForm: {
        id: null,
        proforma_id: null,
        type: null,
        mode: null,
        percent: null,
        value: null,
        vat: null,
        include_order_items: false,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
      },
      datas: null,
      vatItems: [
        'TAHK', 'TAM', 'AAM', 'EUT', 'EUKT', 'F.AFA', 'K.AFA', 'HO', 'EUE',
        'EUFADE', 'EUFAD37', 'ATK', 'NAM', 'EAM', 'KBAUK', 'KBAET',
        '0', '1', '2', '2.1', '3', '4', '4.8', '5', '5.5', '6', '7', '7.7',
        '8', '8.1', '9', '9.5', '10', '11', '12', '13', '13.5', '14', '15',
        '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '25.5',
        '26', '27',
      ],
      typeItems: [
        'Előleg',
        'Adminisztrációs díj',
      ],
    }
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    itemVatCodes() {
      return this.collectItemVatCodes()
    },

    hasMixedItemVat() {
      return this.itemVatCodes.length > 1
    },

    itemVatSummary() {
      return this.itemVatCodes.join(', ')
    },

    selectedProformaMode() {
      if (!this.selectedProforma) return null

      const payload = this.normalizeDocumentPayload(this.selectedProforma)
      const storedMode = String(payload.advance_mode || '').toLowerCase()

      if (storedMode === 'itemized' || storedMode === 'single') {
        return storedMode
      }

      if (payload.include_order_items !== undefined) {
        return this.normalizeBoolean(payload.include_order_items) ? 'itemized' : 'single'
      }

      const items = Array.isArray(payload.document_items) ? payload.document_items : []
      const containsSalesOrderItems = items.some(item => item?.sales_order_item_id || item?.item_id)

      return containsSalesOrderItems ? 'itemized' : 'single'
    },

    advanceModeLocked() {
      return Boolean(this.selectedProforma)
    },

    selectedProformaCompatibilityError() {
      if (!this.selectedProforma) return null

      const payload = this.normalizeDocumentPayload(this.selectedProforma)
      const items = Array.isArray(payload.document_items) ? payload.document_items : []

      if (this.selectedProformaMode === 'itemized' && !items.length) {
        return 'The selected itemized proforma does not contain its saved item breakdown, so a deposit invoice cannot be issued safely.'
      }

      if (this.hasMixedItemVat) {
        const breakdown = Array.isArray(payload.vat_breakdown) ? payload.vat_breakdown : []
        const proformaVatCodes = [...new Set(
          breakdown
            .map(line => this.normalizeItemVat(line?.tax_rate || line?.vat))
            .filter(Boolean)
        )].sort()
        const orderVatCodes = [...this.itemVatCodes].sort()

        if (JSON.stringify(proformaVatCodes) !== JSON.stringify(orderVatCodes)) {
          return 'The selected proforma does not contain the complete VAT-rate breakdown of the current Sales Order. Delete or cancel the old proforma and create a new VAT-grouped or itemized proforma.'
        }
      }

      return null
    },

    isValid() {
      if (this.selectedProformaCompatibilityError) return false
      if (!this.localForm.type) return false
      if (!this.localForm.mode) return false
      if (!this.localForm.include_order_items && !this.hasMixedItemVat && !this.isValidVat(this.localForm.vat)) return false

      if (this.localForm.mode === 'percent') {
        return (
          this.localForm.percent !== null &&
          this.localForm.percent !== '' &&
          Number(this.localForm.percent) > 0 &&
          Number(this.localForm.percent) <= 100
        )
      }

      if (this.localForm.mode === 'value') {
        return (
          this.localForm.value !== null &&
          this.localForm.value !== '' &&
          Number(this.localForm.value) > 0
        )
      }

      return false
    },

    computeSuffix() {
      return this.datas?.priceList?.currency_id ?? 'HUF'
    },

    proformaOptions() {
      const documents = this.getOrderDocuments()
      const usedProformaIds = this.getActiveDepositProformaIds(documents)
      const selectedId = this.normalizeComparableId(this.localForm.proforma_id)

      return documents
        .filter(document => this.normalizeDocumentType(document.type) === 'proforma')
        .filter(document => !this.isDocumentReversed(document))
        .filter((document) => {
          const documentId = this.normalizeComparableId(document.id)

          return !usedProformaIds.includes(documentId) || documentId === selectedId
        })
        .map(document => ({
          value: document.id,
          text: this.getProformaOptionLabel(document),
          document,
        }))
    },

    selectedProforma() {
      if (!this.localForm.proforma_id) {
        return null
      }

      const selectedId = this.normalizeComparableId(this.localForm.proforma_id)
      const option = this.proformaOptions.find((item) => {
        return this.normalizeComparableId(item.value) === selectedId
      })

      return option ? option.document : null
    },

    previewSettings() {
      return {
        id: this.orderId,
        proforma_id: this.localForm.proforma_id || null,
        type: this.localForm.type,
        mode: this.localForm.mode,
        percent: this.localForm.mode === 'percent'
          ? Number(this.localForm.percent)
          : null,
        value: this.localForm.mode === 'value'
          ? Number(this.localForm.value)
          : null,
        vat: (this.localForm.include_order_items || this.hasMixedItemVat) ? null : (this.localForm.vat || null),
        include_order_items: Boolean(this.localForm.include_order_items),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
      }
    },

    maxAmount() {
      const total = this.invoiceDatas?.total_net
      return total === null || total === undefined || total === ''
        ? null
        : Number(total)
    },

    typeRules() {
      return [
        v => !!v || 'You must select a type',
      ]
    },

    modeRules() {
      return [
        v => !!v || 'You must select a calculation mode',
      ]
    },

    percentRules() {
      return [
        v => this.localForm.mode !== 'percent' || (v !== null && v !== '') || 'You must enter a percentage',
        v => this.localForm.mode !== 'percent' || !isNaN(Number(v)) || 'The percentage must be a number',
        v => this.localForm.mode !== 'percent' || Number(v) >= 0 || 'Minimum 0',
        v => this.localForm.mode !== 'percent' || Number(v) <= 100 || 'Maximum 100(%)',
      ]
    },

    valueRules() {
      return [
        v => this.localForm.mode !== 'value' || (v !== null && v !== '') || 'You must enter an amount',
        v => this.localForm.mode !== 'value' || !isNaN(Number(v)) || 'The amount must be a number',
        v => this.localForm.mode !== 'value' || Number(v) >= 0 || 'Minimum 0',
        v =>
          this.localForm.mode !== 'value' ||
          this.maxAmount === null ||
          Number(v) <= this.maxAmount ||
          `Maximum ${this.maxAmount}`,
      ]
    },

    vatRules() {
      return [
        v => !!v || 'You must select one tax rate',
        v => this.vatItems.includes(String(v)) || 'You can select only the tax rate listed',
      ]
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.syncForm()
        } else {
          this.showPreview = false
        }
      },
    },

    initialData: {
      deep: true,
      handler() {
        if (this.value) {
          this.syncForm()
        }
      },
    },

    invoiceDatas: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.datas = val
          this.enforceAdvanceModeRules()
          this.calculateFAFA()
          this.setInitialTax()
        }
      },
    },

    'localForm.mode'(newMode) {
      if (newMode === 'percent') {
        this.localForm.value = null
      }

      if (newMode === 'value') {
        this.localForm.percent = null
      }
    },

    'localForm.include_order_items'(enabled) {
      if (this.advanceModeLocked) {
        this.$nextTick(() => this.enforceAdvanceModeRules())
        return
      }

      if (!enabled) {
        this.setInitialTax()
      }

      this.calculateFAFA()
    },

    'localForm.proforma_id'(newValue, oldValue) {
      if (!newValue && oldValue) {
        // Csatolás törlésekor ne maradjon bent a díjbekérőtől örökölt tételes mód.
        this.localForm.type = 'Előleg'
        this.localForm.include_order_items = false
        this.localForm.vat = this.initialData?.vat ?? '27'
      }

      this.applySelectedProformaValues()
      this.enforceAdvanceModeRules()
      this.setInitialTax()
    },

    hasMixedItemVat() {
      this.enforceAdvanceModeRules()
    },

    'localForm.vat'(newVat) {
      if (String(newVat).includes('F.AFA')) {
        if (this.localForm.note) {
          this.localForm.note += '\nAz ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
        } else {
          this.localForm.note = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
        }
      }
    },
  },

  methods: {
    normalizeBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeSalesPointIdentifier(value) {
      if (value === null || value === undefined || typeof value === 'object') return ''

      return String(value)
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/[^a-z0-9]/g, '')
    },

    isKismetalVaskerSalesPoint() {
      const salesPoint = this.invoiceDatas?.salesPoint || this.invoiceDatas?.sales_point || {}
      const candidates = [
        salesPoint,
        salesPoint?.name,
        salesPoint?.slug,
        salesPoint?.domain,
        salesPoint?.website,
        salesPoint?.website?.domain,
        salesPoint?.site?.domain,
        this.invoiceDatas?.sales_point,
        this.invoiceDatas?.sales_point_name,
        this.invoiceDatas?.salesPointName,
      ]

      return candidates.some(value => this.normalizeSalesPointIdentifier(value).includes('kismetalvasker'))
    },

    isEurOrder() {
      const orderCurrency = String(
        this.invoiceDatas?.priceList?.currency_id
        || this.invoiceDatas?.price_list?.currency_id
        || this.invoiceDatas?.currency_id
        || ''
      ).trim().toUpperCase()

      if (orderCurrency === 'EUR') return true

      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      const itemCurrencies = items
        .map(item => String(
          item?.currency_id
          || item?.currency?.id
          || item?.priceList?.currency_id
          || item?.price_list?.currency_id
          || ''
        ).trim().toUpperCase())
        .filter(Boolean)

      return itemCurrencies.length > 0 && itemCurrencies.every(currency => currency === 'EUR')
    },

    resolveAutomaticTaxDefault() {
      if (this.isEurOrder()) return 'EUT'

      if (
        this.isKismetalVaskerSalesPoint()
        && this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      ) {
        return 'F.AFA'
      }

      return null
    },

    normalizeDocumentPayload(document) {
      const payload = document?.extra_payload

      if (!payload) return {}
      if (typeof payload === 'object') return payload

      try {
        const parsed = JSON.parse(payload)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    },

    enforceAdvanceModeRules() {
      if (this.selectedProforma) {
        this.localForm.include_order_items = this.selectedProformaMode === 'itemized'

        if (this.localForm.include_order_items || this.hasMixedItemVat) {
          this.localForm.vat = null
        }

        return
      }

      if (this.hasMixedItemVat) {
        // A vegyes ÁFA nem kényszeríti ki a terméktételes módot.
        // Összesített módban az ÁFA-kulcsonkénti bontás automatikus.
        this.localForm.vat = null
      }
    },

    getOrderDocuments() {
      if (!this.invoiceDatas || !Array.isArray(this.invoiceDatas.documents)) {
        return []
      }

      return this.invoiceDatas.documents
    },

    normalizeDocumentType(type) {
      return String(type || '').toLowerCase()
    },

    normalizeComparableId(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }

      return String(value)
    },

    getActiveDepositProformaIds(documents) {
      return documents
        .filter(document => this.normalizeDocumentType(document.type) === 'deposit_invoice')
        .filter(document => !this.isDocumentReversed(document))
        .filter(document => document.proforma_id !== null && document.proforma_id !== undefined)
        .map(document => this.normalizeComparableId(document.proforma_id))
    },

    isDocumentReversed(document) {
      if (!document) {
        return false
      }

      const type = this.normalizeDocumentType(document.type)
      const status = String(document.status || '').toLowerCase()
      const reverseStatus = String(document.reverse_status || '').toLowerCase()

      return type === 'reverse_invoice' ||
        status === 'reversed' ||
        reverseStatus === 'reversed' ||
        !!document.reversed_by_document_id
    },

    getProformaOptionLabel(document) {
      const documentNumber = document.document_number || 'Proforma'
      const amount = this.formatDocumentAmount(document.gross_amount)
      const status = document.status ? String(document.status) : 'unknown'

      return documentNumber + " - " + amount + " - " + status
    },

    formatDocumentAmount(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      const numericValue = Number(value)

      if (Number.isNaN(numericValue)) {
        return value + " " + this.computeSuffix
      }

      return numericValue.toLocaleString("hu-HU") + " " + this.computeSuffix
    },

    applySelectedProformaValues() {
      const proforma = this.selectedProforma

      if (!proforma) {
        return
      }

      const payload = this.normalizeDocumentPayload(proforma)

      if (payload.proforma_type) {
        this.localForm.type = payload.proforma_type
      } else if (proforma.proforma_type) {
        this.localForm.type = proforma.proforma_type
      }

      this.localForm.include_order_items = this.selectedProformaMode === 'itemized'

      if (proforma.percent !== null && proforma.percent !== undefined && proforma.percent !== '') {
        this.localForm.mode = 'percent'
        this.localForm.percent = Number(proforma.percent)
        this.localForm.value = null
      } else if (proforma.net_amount !== null && proforma.net_amount !== undefined && proforma.net_amount !== '') {
        this.localForm.mode = 'value'
        this.localForm.value = Number(proforma.net_amount)
        this.localForm.percent = null
      }

      if (proforma.tax_rate !== null && proforma.tax_rate !== undefined && proforma.tax_rate !== '') {
        const proformaVat = this.normalizeItemVat(proforma.tax_rate)

        if (proformaVat) {
          this.localForm.vat = proformaVat
        } else if (!this.localForm.include_order_items) {
          this.localForm.vat = null
          this.setInitialTax()
        }
      }

      if (!this.localForm.fulfillmentDate) {
        this.localForm.fulfillmentDate = this.normalizeDateValue(
          proforma.paid_at || proforma.payment_deadline || proforma.due_at || proforma.issued_at
        )
      }

      if (!this.localForm.paymentDeadline) {
        this.localForm.paymentDeadline = this.normalizeDateValue(
          proforma.payment_deadline || proforma.due_at || proforma.paid_at
        )
      }

      if (!this.localForm.note && proforma.comment) {
        this.localForm.note = proforma.comment
      }
    },

    normalizeDateValue(value) {
      if (!value) {
        return null
      }

      const normalizedValue = String(value).slice(0, 10)

      if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
        return normalizedValue
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return null
      }

      return this.formatDateForInput(date)
    },

    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    setTodayPlusDays(field, days) {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + days)
      this.localForm[field] = this.formatDateForInput(date)
    },

    normalizeItemVat(value) {
      if (value === null || value === undefined || value === '') return null

      const normalized = String(value).trim().toUpperCase().replace(',', '.')
      return this.vatItems.includes(normalized) ? normalized : null
    },

    resolveItemVat(item) {
      const automaticTax = this.resolveAutomaticTaxDefault()
      const zeroTax = this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      const reverseTax = this.normalizeBoolean(this.invoiceDatas?.reverse_tax)
      const isService = this.normalizeBoolean(item?.service) || this.normalizeBoolean(item?.item?.service)

      if (automaticTax) return automaticTax

      if (zeroTax) {
        const storedVatName = this.normalizeItemVat(item?.vat_name)

        if (storedVatName && Number.isNaN(Number(storedVatName))) {
          return storedVatName
        }

        return '0'
      }

      if (reverseTax && !isService) return 'F.AFA'

      return this.normalizeItemVat(item?.vat_name)
        || this.normalizeItemVat(item?.vat_value)
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || '27'
    },

    collectItemVatCodes() {
      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      return [...new Set(items.map(item => this.resolveItemVat(item)).filter(Boolean))]
    },

    normalizeVat() {
      if (this.localForm.vat === null || this.localForm.vat === '') {
        this.localForm.vat = null
        return
      }

      this.localForm.vat = String(this.localForm.vat).trim().toUpperCase()
    },

    isValidVat(vat) {
      if (vat === null || vat === '') return false
      const normalized = String(vat).trim().toUpperCase()
      return this.vatItems.includes(normalized)
    },

    syncForm() {
      this.vatDefaultApplied = false

      const resolvedMode = this.initialData?.mode ||
        (this.initialData?.value !== null && this.initialData?.value !== undefined
          ? 'value'
          : 'percent')

      this.localForm = {
        id: this.orderId,
        // A kézi előlegszámla alapból önálló, a díjbekérő csatolása opcionális.
        proforma_id: null,
        // Minden új megnyitás Előleg / összesített módból induljon.
        type: 'Előleg',
        mode: resolvedMode,
        percent: this.initialData?.percent ?? null,
        value: this.initialData?.value ?? null,
        vat: this.initialData?.vat ?? '27',
        include_order_items: false,
        fulfillmentDate: this.initialData?.fulfillmentDate || null,
        paymentDeadline: this.initialData?.paymentDeadline || null,
        note: this.initialData?.note || null,
      }

      this.normalizeVat()
      this.applySelectedProformaValues()
      this.enforceAdvanceModeRules()
      this.calculateFAFA()
      this.setInitialTax()
    },

    openPreview() {
      this.enforceAdvanceModeRules()

      if (this.selectedProformaCompatibilityError) {
        Swal.fire('Cannot create deposit invoice', this.selectedProformaCompatibilityError, 'error')
        return
      }

      if (this.showPreview) {
        this.showPreview = false
        return
      }

      this.normalizeVat()

      const valid = this.$refs.depositForm.validate()

      if (!valid) {
        return
      }

      this.showPreview = true
    },

    closeDialog() {
      this.showPreview = false
      this.syncForm()
      this.dialogProxy = false
    },

    submit() {
      this.enforceAdvanceModeRules()

      if (this.selectedProformaCompatibilityError) {
        Swal.fire('Cannot create deposit invoice', this.selectedProformaCompatibilityError, 'error')
        return
      }

      this.normalizeVat()

      const valid = this.$refs.depositForm.validate()

      if (!valid) {
        return
      }

      const payload = {
        id: this.orderId,
        proforma_id: this.localForm.proforma_id || null,
        type: this.localForm.type,
        mode: this.localForm.mode,
        percent: this.localForm.mode === 'percent'
          ? Number(this.localForm.percent)
          : null,
        value: this.localForm.mode === 'value'
          ? Number(this.localForm.value)
          : null,
        vat: (this.localForm.include_order_items || this.hasMixedItemVat) ? null : (this.localForm.vat || null),
        include_order_items: Boolean(this.localForm.include_order_items),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
      }

      Swal.fire({
        title: '<strong>ALERT</strong>',
        icon: 'warning',
        width: '600px',
        text: 'Are you sure you want to create this deposit invoice? This action cannot be undone.',
        showCloseButton: true,
        focusConfirm: false,
        confirmButtonText: 'Yes, create it!',
        confirmButtonColor: '#00c300',
        infoColor: '#e33354',
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('save', payload)
          //this.dialogProxy = false
        }
      })
    },

    calculateFAFA() {
      if (!this.invoiceDatas) return

      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
      const usesReverseVat = (this.localForm.include_order_items || this.hasMixedItemVat)
        ? this.itemVatCodes.includes('F.AFA')
        : this.normalizeItemVat(this.localForm.vat) === 'F.AFA'

      if (usesReverseVat && (!this.localForm.note || !this.localForm.note.includes(text))) {
        this.localForm.note = this.localForm.note
          ? this.localForm.note + '\n' + text
          : text
      }
    },

    setInitialTax() {
      if (!this.invoiceDatas || this.vatDefaultApplied) return

      // Kapcsolt díjbekérőnél annak mentett ÁFA-beállítása az irányadó és a mező zárolt.
      if (this.selectedProforma) {
        this.vatDefaultApplied = true
        return
      }

      if (this.localForm.include_order_items || this.hasMixedItemVat) {
        if (this.hasMixedItemVat) {
          this.localForm.vat = null
        }
        this.vatDefaultApplied = true
        return
      }

      const automaticTax = this.resolveAutomaticTaxDefault()

      this.localForm.vat = automaticTax
        || this.normalizeItemVat(this.localForm.vat)
        || (this.itemVatCodes.includes('27') ? '27' : this.itemVatCodes[0])
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || '27'

      this.vatDefaultApplied = true
    },


  },
}
</script>

<style scoped>
.deposit-dialog-layout {
  display: flex;
  align-items: stretch;
  min-height: 620px;
}

.deposit-form-pane {
  flex: 0 0 560px;
  max-width: 560px;
}

.deposit-preview-pane {
  display: flex;
  flex: 1;
  min-width: 0;
  background: #fafafa;
}

.preview-pane-inner {
  flex: 1;
  min-width: 0;
  overflow: auto;
}
</style>